import type { QuickInsertHandler } from '@atlaskit/editor-common/types/quick-insert';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { Snippet } from '@atlassian/editor-snippets-provider/types';

import { isAdfNodeCompatibleWithSchema } from '../../pm-plugins/schemaCompatibility';
import type { SnippetsPlugin, SnippetsPluginOptions } from '../../snippetsPluginType';
import { filterEligibleSnippets, selectEligibleSnippets } from '../../utils/selectEligibleSnippets';
import { getSnippetQuickInsertComponents } from './getSnippetQuickInsertComponents';
import { getTemplateGalleryQuickInsertItem } from './getTemplateGalleryQuickInsertItem';
import { getSnippetQuickInsertKey, mapToQuickInsertItem } from './mapToQuickInsertItem';

type RegisterConfluenceSnippetsParams = {
	api: Parameters<SnippetsPlugin>[0]['api'];
	openTemplateGallery: SnippetsPluginOptions['openTemplateGallery'];
	replaceExisting?: boolean;
	snippets: Snippet[];
	tr: Transaction;
};

export const registerConfluenceSnippets = ({
	api,
	openTemplateGallery,
	replaceExisting = false,
	snippets,
	tr,
}: RegisterConfluenceSnippetsParams): Transaction | null => {
	const removedSnippetIds = api?.snippets?.sharedState.currentState()?.removedSnippetIds;
	const compatibleSnippets = snippets.filter((snippet) => {
		if (removedSnippetIds?.has(snippet.snippetId)) {
			return false;
		}

		try {
			if (!isAdfNodeCompatibleWithSchema(snippet.body, tr.doc.type.schema)) {
				return false;
			}

			const snippetNode = tr.doc.type.schema.nodeFromJSON(snippet.body);
			if (snippetNode.childCount > 0) {
				snippetNode.check();
			}
			return true;
		} catch {
			return false;
		}
	});
	const isSlashCommandEnabled = isExperimentEnabled('platform_editor_slash_command');

	if (!isSlashCommandEnabled) {
		if (!api?.quickInsert) {
			return null;
		}

		const eligibleSnippets = selectEligibleSnippets(compatibleSnippets);
		const quickInsertItems: QuickInsertHandler = (intl) => [
			...(openTemplateGallery
				? [getTemplateGalleryQuickInsertItem(openTemplateGallery, intl)]
				: []),
			...eligibleSnippets.map((snippet) => mapToQuickInsertItem(snippet, api, intl)),
		];
		if (replaceExisting && eligibleSnippets.length === 1) {
			const snippetKey = getSnippetQuickInsertKey(eligibleSnippets[0].snippetId);
			const { lazyDefaultItems, providedItems } = api.quickInsert.sharedState.currentState() ?? {};
			const hasExistingSnippet = [...(lazyDefaultItems?.() ?? []), ...(providedItems ?? [])].some(
				(item) => item.key === snippetKey,
			);
			if (hasExistingSnippet) {
				return api.quickInsert.commands.updateQuickInsertItem(snippetKey, quickInsertItems)({ tr });
			}
		}
		return api.quickInsert.commands.addQuickInsertItem(quickInsertItems)({ tr });
	}

	const snippetQuickInsertComponents = getSnippetQuickInsertComponents({
		api,
		getItem: mapToQuickInsertItem,
		snippets: filterEligibleSnippets(compatibleSnippets),
	});

	api?.uiControlRegistry?.actions.register(snippetQuickInsertComponents, {
		replaceExisting,
	});
	return tr;
};
